import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { Link } from "@tanstack/react-router";
import {
  FileText,
  Database,
  Search,
  Shield,
  ShieldCheck,
  MessageSquare,
  BarChart3,
  ArrowRight,
  BrainCircuit,
  Eye,
  Users,
  FileSpreadsheet,
  BookOpen,
  CalendarClock,
} from "lucide-react";

import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Section } from "@/components/marketing/layout/Section";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import KbHeroes from "@/components/marketing/visuals/KbHeroes";
import { MobileStickyCta } from "@/components/marketing/MobileStickyCta";

export const Route = createFileRoute("/products/knowledge-base")({
  component: KnowledgeBasePage,
});

const SOFTWARE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Larasoft Knowledge Base",
  description:
    "Ask questions of your documents and databases using natural language. Larasoft Knowledge Base delivers AI-powered insights with enterprise-grade security.",
  url: "https://larasoft.global/products/knowledge-base",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  provider: {
    "@type": "Organization",
    name: "Larasoft",
    url: "https://larasoft.global",
  },
};

const METRICS = [
  { label: "Document types", value: "Multiple" },
  { label: "Per-user fees", value: "None" },
  { label: "Data sources", value: "Files & SQL" },
  { label: "Security", value: "Enterprise" },
];

const FILE_FEATURES = [
  {
    icon: FileText,
    title: "Multiple document types",
    description:
      "Upload PDFs, Word documents, and PowerPoint presentations. Content is chunked and indexed automatically, with spreadsheet support coming soon.",
  },
  {
    icon: Search,
    title: "Semantic search",
    description:
      "Find relevant passages across hundreds of documents using meaning, not just keywords - including tabular data within PDFs that other tools miss.",
  },
  {
    icon: ShieldCheck,
    title: "No hallucinations",
    description:
      "The AI answers only from your uploaded documents. When it can't find an answer, it tells you - it never guesses or makes things up.",
  },
  {
    icon: MessageSquare,
    title: "Cited answers",
    description:
      "Every answer includes citations showing exactly which document and where. Click through to verify the source yourself.",
  },
  {
    icon: BookOpen,
    title: "Intentional curation",
    description:
      "You choose which documents to include - no noisy SharePoint dumps. Quality-controlled knowledge means reliable answers.",
  },
  {
    icon: Shield,
    title: "Complete data privacy",
    description:
      "Documents are stored encrypted in Azure. Only statistical embeddings reach the AI - your actual content never leaves your environment.",
  },
];

const FILE_USE_CASES = [
  "Policy compliance - instant answers from HR and governance documents",
  "Onboarding knowledge - new staff find answers without asking colleagues",
  "Contract analysis - query terms, obligations, and renewal dates",
  "Standard operating procedures - field teams get answers on the spot",
  "Marketing content - check what already exists before creating new material",
  "Technical documentation - from medical procedures to engineering standards",
];

const DB_FEATURES = [
  {
    icon: MessageSquare,
    title: "Natural language to SQL",
    description:
      "Ask business questions in plain English. The AI translates them into precise SQL queries - no technical skills required.",
  },
  {
    icon: BookOpen,
    title: "Business glossary",
    description:
      'Teach the system your terminology - "revenue means ex-VAT", "active customer means billed in the last 90 days". It learns your business over time.',
  },
  {
    icon: BarChart3,
    title: "Visual results",
    description:
      "Answers are returned as formatted tables and summaries, ready for decision-making.",
  },
  {
    icon: FileSpreadsheet,
    title: "One-click reports",
    description:
      "Like an answer? Turn it into a recurring report sent to the right people on a schedule - daily, weekly, or monthly.",
  },
  {
    icon: CalendarClock,
    title: "BI Lite",
    description:
      "Pin useful queries as scheduled reports. Operational business intelligence without the complexity or cost of a full BI platform.",
  },
  {
    icon: Eye,
    title: "Full audit trail",
    description:
      "Every question and generated query is logged for compliance and review. No data rows ever leave your environment.",
  },
];

const DB_USE_CASES = [
  "Management reporting - ask for KPIs without waiting for the data team",
  "Ad-hoc data exploration - investigate anomalies with conversational queries",
  "Self-serve analytics - empower non-technical staff to query their own data",
  "Board preparation - quickly pull figures for governance and compliance packs",
  'Financial trends - "What are the top 10 trends in our finances?"',
  "Operational insights - query across finance, service, HR, and sales data in one place",
];

const HOW_IT_WORKS = [
  {
    step: "1",
    icon: Database,
    title: "Connect",
    description:
      "Point Knowledge Base at your documents or databases. Setup takes minutes, not weeks.",
  },
  {
    step: "2",
    icon: MessageSquare,
    title: "Ask",
    description:
      "Type a question in natural language. No query syntax, no training required.",
  },
  {
    step: "3",
    icon: BrainCircuit,
    title: "Insight",
    description:
      "Receive accurate, cited answers with full traceability back to the source.",
  },
];

function KnowledgeBasePage() {
  return (
    <MarketingLayout>
      <Helmet>
        <title>
          Knowledge Base - AI-powered document and data insights | Larasoft
        </title>
        <meta
          name="description"
          content="Ask questions of your documents and databases using natural language. Larasoft Knowledge Base delivers AI-powered insights with enterprise-grade security."
        />
        <link
          rel="canonical"
          href="https://larasoft.global/products/knowledge-base"
        />
        <script type="application/ld+json">
          {JSON.stringify(SOFTWARE_SCHEMA)}
        </script>
      </Helmet>

      {/* Hero */}
      <Section
        containerClassName="grid grid-cols-1 items-center gap-8 md:grid-cols-[2fr_3fr]"
        className="py-20"
      >
        <div className="space-y-6">
          <Badge className="w-max" variant="info">
            Knowledge Base
          </Badge>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight text-brand-blue-dark">
            Your business knowledge, accessible to everyone
          </h1>
          <div className="space-y-3 text-slate-600">
            <p>
              You shouldn't need to be a specialist report writer to understand
              your business data, or the resident policy expert to answer a
              question about your own procedures.{" "}
              <span className="font-semibold text-brand-blue-dark">
                Knowledge Base
              </span>{" "}
              gives every team member natural-language access to your
              organisation's institutional memory - across{" "}
              <span className="font-semibold text-brand-blue-dark">
                multiple document types
              </span>{" "}
              and your{" "}
              <span className="font-semibold text-brand-blue-dark">
                existing databases
              </span>
              .
            </p>
            <p>
              Policies, procedures, business methodologies, financial data - ask
              a question in plain English and get a cited answer in seconds. Two
              editions so you can start with what matters most.
            </p>
            <p className="font-semibold text-slate-800">
              No per-user pricing. Pay for the knowledge base, not the number of
              people who need answers.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button asChild>
              <Link to="/contact">Request trial</Link>
            </Button>
            <Button variant="outline" asChild>
              <a href="#how-it-works">Learn more</a>
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4 text-xs text-slate-600 sm:grid-cols-4">
            {METRICS.map((metric) => (
              <div
                key={metric.label}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm"
              >
                <div className="text-sm font-semibold text-slate-900">
                  {metric.value}
                </div>
                <div>{metric.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative overflow-hidden rounded-2xl">
          <KbHeroes />
        </div>
      </Section>

      <MobileStickyCta />

      {/* File Edition */}
      <Section containerClassName="space-y-8" className="py-16 bg-white/50">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-blue-pale/70 text-brand-blue-dark">
              <FileText className="h-5 w-5" />
            </span>
            <h2 className="text-2xl font-bold text-brand-blue-dark">File Edition</h2>
          </div>
          <p className="max-w-3xl text-slate-600">
            For organisations with valuable information locked in documents -
            policies, contracts, research, onboarding materials. Upload once, ask
            questions forever. Versioning built in so you can query the latest
            policy or compare across years.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FILE_FEATURES.map(({ icon: Icon, title, description }) => (
            <Card
              key={title}
              className="h-full border-white/60 bg-white/70 shadow-sm backdrop-blur"
            >
              <CardContent className="space-y-3">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-blue-pale/70 text-brand-blue-dark">
                  <Icon className="h-6 w-6" />
                </span>
                <CardTitle>{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="rounded-xl border border-brand-blue-pale bg-brand-blue-pale/30 px-6 py-4">
          <p className="text-sm text-slate-700">
            <span className="font-semibold text-brand-blue-dark">
              Over 100 pages uploaded?
            </span>{" "}
            At that point, Knowledge Base consistently outperforms any single
            person's ability to recall the right answer from across your
            documents.
          </p>
        </div>

        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-slate-800">Use cases</h3>
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {FILE_USE_CASES.map((item) => (
              <li
                key={item}
                className="flex items-start gap-2 text-sm text-slate-600"
              >
                <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue-dark" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* Database Edition */}
      <Section containerClassName="space-y-8" className="py-16">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <Database className="h-5 w-5" />
            </span>
            <h2 className="text-2xl font-bold text-brand-blue-dark">Database Edition</h2>
            <Badge variant="outline" className="text-xs">
              Beta
            </Badge>
          </div>
          <p className="max-w-3xl text-slate-600">
            For organisations wanting AI-powered data insights without SQL
            expertise. Connect your databases, let your team ask questions in
            plain English, and turn answers into scheduled reports - no analyst
            or BI specialist required. Extended trial available during the beta
            programme.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {DB_FEATURES.map(({ icon: Icon, title, description }) => (
            <Card
              key={title}
              className="h-full border-white/60 bg-white/70 shadow-sm backdrop-blur"
            >
              <CardContent className="space-y-3">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                  <Icon className="h-6 w-6" />
                </span>
                <CardTitle>{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-slate-800">Use cases</h3>
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {DB_USE_CASES.map((item) => (
              <li
                key={item}
                className="flex items-start gap-2 text-sm text-slate-600"
              >
                <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* How It Works */}
      <Section
        id="how-it-works"
        containerClassName="space-y-8"
        className="py-16 bg-white/50 scroll-mt-24"
      >
        <div className="space-y-3 text-center">
          <h2 className="text-2xl font-bold text-brand-blue-dark">How it works</h2>
          <p className="text-sm text-slate-600">
            Three steps from data to insight
          </p>
        </div>
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
          {HOW_IT_WORKS.map(({ step, icon: Icon, title, description }) => (
            <div key={step} className="text-center space-y-4">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-blue-dark text-white text-xl font-bold shadow-lg">
                {step}
              </span>
              <div className="inline-flex items-center gap-2">
                <Icon className="h-5 w-5 text-brand-blue-dark" />
                <h3 className="text-lg font-semibold">{title}</h3>
              </div>
              <p className="text-sm text-slate-600">{description}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* CTA Band */}
      <Section
        containerClassName="text-center space-y-6"
        className="py-16 bg-gradient-to-r from-brand-blue-dark via-brand-blue-medium to-brand-blue-dark text-white"
      >
        <div className="flex items-center justify-center gap-3">
          <Users className="h-8 w-8" />
          <h2 className="text-2xl sm:text-3xl font-bold">
            Ready to unlock your organisation's knowledge?
          </h2>
        </div>
        <p className="mx-auto max-w-xl text-white/85">
          Whether you start with documents or databases, Knowledge Base gives
          your team instant, AI-powered answers from data they already have.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Button
            asChild
            className="bg-white text-brand-blue-dark hover:bg-white/90"
          >
            <Link to="/contact">Get in touch</Link>
          </Button>
          <Button
            asChild
            className="bg-white text-brand-blue-dark hover:bg-white/90"
          >
            <Link to="/">Explore more products</Link>
          </Button>
        </div>
      </Section>
    </MarketingLayout>
  );
}
